import React, { useState, useRef } from 'react';
import { Creatable } from 'react-select';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Colxx } from './CustomBootstrap';
import CustomSelectInput from './CustomSelectInput';
import ImageUploader from 'react-images-upload';
import Resizer from 'react-image-file-resizer';
import { storage } from '../../helpers/Firebase';

const CreatableTintingSides = ({
    selectedTintingSides = [],
    availableTintingSides = [],
    onChange,
    onCreateTintingSide,
    disabled = false,
    placeholder = "Select tinting sides...",
    className = "react-select",
    collection = 'services',
    parentId = null
}) => {
    const [newTintingSideModal, setNewTintingSideModal] = useState(false);
    const [newTintingSide, setNewTintingSide] = useState({ name: '', name_ar: '', key: '', edit: false, image: null });
    const [tintingSidePicture, setTintingSidePicture] = useState(null);
    const isProcessingImage = useRef(false);

    // Group styles for react-select
    const groupStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const groupBadgeStyles = {
        backgroundColor: '#EBECF0',
        borderRadius: '2em',
        color: '#172B4D',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 'normal',
        lineHeight: '1',
        minWidth: 1,
        padding: '0.16666666666667em 0.5em',
        textAlign: 'center',
    };

    const formatGroupLabel = (data) => (
        <div style={groupStyles}>
            <span>{data.label}</span>
            <span style={groupBadgeStyles}>{data.options?.length || 0}</span>
        </div>
    );

    // Handle selection change
    const handleChange = (selectedOption) => {
        if (selectedOption) {
            const newSelectedSides = [...selectedTintingSides];
            newSelectedSides.push({
                name: selectedOption.name,
                name_ar: selectedOption.name_ar,
                key: selectedOption.key
            });
            onChange && onChange(newSelectedSides);
        }
    };

    // Generate ID from name (slug-like format)
    const generateIdFromName = (name) => {
        if (!name) return '';
        return name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '_')  // Replace non-alphanumeric with underscore
            .replace(/^_+|_+$/g, '')       // Remove leading/trailing underscores
            .substring(0, 50);             // Limit length
    };

    // Handle image upload for tinting side
    const onDropTintingSideImage = (picture) => {
        // Check if picture array exists and has items
        if (picture && Array.isArray(picture) && picture.length > 0) {
            let lastPic = picture[picture.length - 1];

            // Only process if it's actually a file
            if (lastPic instanceof File || lastPic instanceof Blob) {
                isProcessingImage.current = true;
                Resizer.imageFileResizer(
                    lastPic,
                    1080,
                    1080,
                    'png',
                    100,
                    0,
                    uri600 => {
                        const newPicture = {
                            name: '600',
                            data: uri600
                        };
                        setTintingSidePicture([newPicture]);
                        // Store preview URL for immediate display
                        const previewUrl = URL.createObjectURL(lastPic);
                        // Use functional update to avoid stale closure
                        setNewTintingSide(prevState => ({ ...prevState, image: previewUrl }));
                        isProcessingImage.current = false;
                    },
                    'blob'
                );
            }
        } else {
            // Empty array - user removed the image
            // Only clear if we're not currently processing an image
            if (!isProcessingImage.current) {
                setNewTintingSide(prevState => {
                    if (prevState.image) {
                        // Revoke the object URL to free memory
                        if (prevState.image.startsWith('blob:')) {
                            URL.revokeObjectURL(prevState.image);
                        }
                        return { ...prevState, image: null };
                    }
                    return prevState;
                });
                setTintingSidePicture(null);
            }
        }
    };

    // Handle creating new tinting side
    const handleCreateOption = (inputValue) => {
        const autoGeneratedKey = generateIdFromName(inputValue);
        setNewTintingSide({ name: inputValue, name_ar: '', key: autoGeneratedKey, edit: false });
        setNewTintingSideModal(true);
    };

    // Upload image and get download URL
    const uploadImageAndGetUrl = async (pictureData, tintingSideKey) => {
        return new Promise((resolve, reject) => {
            const metadata = {
                cacheControl: 'public,max-age=300',
                contentType: 'image/png'
            };

            // Create a unique path for the tinting side image
            const imagePath = `tintingSides/${tintingSideKey}_${Date.now()}.png`;
            const storageRef = storage().ref().child(imagePath);
            const uploadTask = storageRef.put(pictureData.data, metadata);

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    // Progress tracking (optional)
                },
                (error) => {
                    console.error('Error uploading image:', error);
                    reject(error);
                },
                () => {
                    // Upload completed successfully
                    uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
                        resolve(downloadURL);
                    }).catch((error) => {
                        console.error('Error getting download URL:', error);
                        reject(error);
                    });
                }
            );
        });
    };

    // Save new tinting side
    const saveNewTintingSide = async () => {
        if (!newTintingSide.name) return;

        // Auto-generate key from name if missing (only for new items, not on update)
        let finalKey = newTintingSide.key;
        if (!finalKey && !newTintingSide.edit) {
            finalKey = generateIdFromName(newTintingSide.name);
        }

        let imageUrl = newTintingSide.image || null;

        // Upload image if available and get the download URL
        if (tintingSidePicture && tintingSidePicture.length > 0) {
            try {
                imageUrl = await uploadImageAndGetUrl(tintingSidePicture[0], finalKey);
            } catch (error) {
                console.error('Failed to upload image:', error);
                // Continue without image if upload fails
            }
        }

        const newTintingSideData = {
            name: newTintingSide.name,
            name_ar: newTintingSide.name_ar,
            key: finalKey,
            image: imageUrl
        };

        let newData = [...selectedTintingSides];

        if (newTintingSide.edit) {
            // Edit existing - preserve existing image if not changed
            newData = newData.map(item => {
                if (item.key === newTintingSide.key) {
                    return {
                        ...newTintingSideData,
                        // Preserve existing image if new image wasn't uploaded
                        image: imageUrl || item.image || null
                    };
                }
                return item;
            });
        } else {
            // Add new
            newData.push(newTintingSideData);
        }

        onChange && onChange(newData);
        onCreateTintingSide && onCreateTintingSide(newTintingSideData);

        setNewTintingSideModal(false);
        setTintingSidePicture(null);
        setNewTintingSide({ name: '', name_ar: '', key: '', edit: false, image: null });
    };

    // Remove tinting side
    const removeTintingSide = (tintingSideKey) => {
        const newData = selectedTintingSides.filter(item => item.key !== tintingSideKey);
        onChange && onChange(newData);
    };

    // Edit tinting side
    const editTintingSide = (tintingSide) => {
        setNewTintingSide({
            name: tintingSide.name,
            name_ar: tintingSide.name_ar,
            key: tintingSide.key,
            edit: true,
            image: tintingSide.image || null
        });
        setTintingSidePicture(null);
        setNewTintingSideModal(true);
    };

    // Get available options (filtered to exclude already selected)
    const getAvailableOptions = () => {
        const selectedKeys = selectedTintingSides.map(side => side.key);
        return availableTintingSides.filter(side => !selectedKeys.includes(side.key));
    };

    return (
        <div className="creatable-tinting-sides-wrapper">
            {/* React Select Component */}
            <Creatable
                formatGroupLabel={formatGroupLabel}
                components={{ Input: CustomSelectInput }}
                className={className}
                options={getAvailableOptions()}
                value=""
                onChange={handleChange}
                onCreateOption={handleCreateOption}
                isDisabled={disabled}
                placeholder={placeholder}
                isClearable
            />

            {/* Selected Tinting Sides Display */}
            {selectedTintingSides.length > 0 && (
                <div className="mb-2 mt-2" style={{
                    minHeight: 'auto',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                }}>
                    {selectedTintingSides.map((tintingSide, index) => (
                        <div key={index} style={{
                            margin: '5px',
                            padding: '8px 12px',
                            border: '1px solid #d7d7d7',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            {tintingSide.image && (
                                <img 
                                    src={tintingSide.image} 
                                    alt={tintingSide.name}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        objectFit: 'cover',
                                        borderRadius: '4px'
                                    }}
                                />
                            )}
                            <div style={{ marginRight: '4px' }}>{tintingSide.name}</div>
                            <i
                                style={{ cursor: 'pointer', marginRight: '5px' }}
                                className="simple-icon-close"
                                onClick={() => removeTintingSide(tintingSide.key)}
                                title="Remove tinting side"
                            />
                            <i
                                style={{ cursor: 'pointer' }}
                                className="simple-icon-pencil"
                                onClick={() => editTintingSide(tintingSide)}
                                title="Edit tinting side"
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* New/Edit Tinting Side Modal */}
            <Modal backdrop="static" isOpen={newTintingSideModal} toggle={() => {
                setNewTintingSideModal(false);
                setTintingSidePicture(null);
                setNewTintingSide({ name: '', name_ar: '', key: '', edit: false, image: null });
                isProcessingImage.current = false;
            }}>
                <ModalHeader toggle={() => {
                    setNewTintingSideModal(false);
                    setTintingSidePicture(null);
                    setNewTintingSide({ name: '', name_ar: '', key: '', edit: false, image: null });
                    isProcessingImage.current = false;
                }}>
                    {newTintingSide.edit ? 'Edit Tinting Side' : 'Create New Tinting Side'}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        {[
                            { key: 'name', title: 'Side Name', width: '12', type: 'text' },
                            { key: 'name_ar', title: 'Side Arabic Name', width: '12', type: 'text' },
                        ].map((item, index) => (
                            <Colxx key={index} xxs="12" md={item.width} className="mb-4">
                                <Label for={item.key}>{item.title}</Label>
                                <Input
                                    type={item.type}
                                    id={item.key}
                                    placeholder={"e.g. " + item.title}
                                    value={newTintingSide[item.key]}
                                    className="customInput"
                                    disabled={item.disabled}
                                    onChange={(e) => {
                                        // Auto-generate key from name only when creating (not editing)
                                        const updatedTintingSide = { ...newTintingSide, [item.key]: e.target.value };
                                        if (!newTintingSide.edit && item.key === 'name') {
                                            updatedTintingSide.key = generateIdFromName(e.target.value);
                                        }
                                        setNewTintingSide(updatedTintingSide);
                                    }}
                                />
                            </Colxx>
                        ))}
                        {!newTintingSide.edit && newTintingSide.key && (
                            <Colxx xxs="12" md="12" className="mb-4">
                                <Label>Side ID</Label>
                                <Input
                                    type="text"
                                    value={newTintingSide.key}
                                    className="customInput"
                                    disabled={true}
                                />
                                <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                                    Auto-generated from name
                                </small>
                            </Colxx>
                        )}
                        <Colxx xxs="12" md="12" className="mb-4">
                            <Label>Tinting Side Image</Label>
                            <ImageUploader
                                className={'w-100'}
                                withIcon={true}
                                label={'Max file size: 15mb, accepted: jpg|gif|png'}
                                buttonText='Choose image'
                                onChange={onDropTintingSideImage}
                                imgExtension={['.jpg', '.gif', '.png', '.jpeg']}
                                maxFileSize={15728640}
                                withPreview={true}
                                singleImage={true}
                                defaultImages={newTintingSide?.image ? [newTintingSide.image] : []}
                            />
                        </Colxx>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={() => {
                        setNewTintingSideModal(false);
                        setTintingSidePicture(null);
                        setNewTintingSide({ name: '', name_ar: '', key: '', edit: false, image: null });
                        isProcessingImage.current = false;
                    }}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        disabled={!newTintingSide.name}
                        onClick={saveNewTintingSide}
                    >
                        {newTintingSide.edit ? 'Update' : 'Add'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CreatableTintingSides;
